import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { DragDropContext } from "react-beautiful-dnd";
import { Scrollbars } from "react-custom-scrollbars-2";

import { makeStyles } from "@mui/styles";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";

// My Files
import { DatabaseContext, UserContext } from "../../../../context";
import ToolbarWithLogo from "../../../ToolbarWithLogo";
import firebase from "../../../../firebase";
import FinderColumn from "./FinderColumn";
import {
    jsonFromArray,
    makeid,
    shouldFileBeVisible,
} from "../../../../utility";

const FAVOURITES_ID = "faves_fggh2973jgbvbiu";

export function getFilesForFinderColumn(
    columnId,
    files,
    groups,
    currentUserData,
) {
    const favouriteKeys = new Set(currentUserData?.faveFiles || []);

    return (files || []).filter((file) => {
        const belongsInColumn =
            columnId === FAVOURITES_ID
                ? favouriteKeys.has(file.key)
                : file.folder === columnId;

        return (
            belongsInColumn &&
            shouldFileBeVisible(file, groups, currentUserData)
        );
    });
}

const useStyles = makeStyles((theme) => ({
    root: {
        margin: 0,
        // ToolbarWithLogo already adds the space for its fixed bottom bar.
        padding: "15px 0 0",
        [theme.breakpoints.down("sm")]: {
            paddingBottom: 0,
        },
    },
    scrollbar: {
        width: "100% !important",
        height: "calc(100dvh - 145px) !important",
        [theme.breakpoints.down("sm")]: {
            height: "calc(100dvh - 75px) !important",
        },
    },
    columnsContainer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        minWidth: "100%",
        height: "100%",
        minHeight: 0,
        backgroundColor: theme.palette.background.default,
    },
    column: {
        flex: "0 0 24%",
        minWidth: "24%",
        maxWidth: "24%",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        [theme.breakpoints.down("sm")]: {
            flexBasis: "100%",
            minWidth: "100%",
            maxWidth: "100%",
            scrollSnapAlign: "start",
            scrollSnapStop: "always",
        },
    },
    columnEntering: {
        // Do not retain the final transform: react-beautiful-dnd positions the
        // dragged row relative to the viewport, and a transformed ancestor
        // would make the row fixed to (and clipped by) this column instead.
        animation: "$columnEnter 220ms cubic-bezier(0.2, 0, 0, 1)",
        "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
        },
    },
    spacerColumn: {
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },
    scrollView: {
        overflowY: "hidden !important",
        WebkitOverflowScrolling: "touch",
        overscrollBehaviorX: "contain",
        [theme.breakpoints.down("sm")]: {
            scrollSnapType: "x mandatory",
        },
    },
    "@keyframes columnEnter": {
        from: {
            opacity: 0,
            transform: "translateX(12px)",
        },
        to: {
            opacity: 1,
            transform: "translateX(0)",
        },
    },
}));

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

function FinderLoadingState() {
    return (
        <Box
            role="status"
            aria-live="polite"
            aria-label="Loading files and folders"
            sx={{
                height: {
                    xs: "calc(100dvh - 75px)",
                    sm: "calc(100dvh - 145px)",
                },
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                px: { xs: 1.5, sm: 2.5 },
                py: { xs: 2, sm: 3 },
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.25,
                    mb: { xs: 2, sm: 2.5 },
                }}
            >
                <CircularProgress size={22} thickness={4} />
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        Loading your files…
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Gathering folders and file details.
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "minmax(0, 1fr)",
                        sm: "repeat(2, minmax(0, 1fr))",
                        md: "repeat(3, minmax(0, 1fr))",
                        lg: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: 1.25,
                }}
            >
                {[0, 1, 2, 3].map((columnIndex) => (
                    <Box
                        key={columnIndex}
                        sx={{
                            display: {
                                xs: columnIndex === 0 ? "block" : "none",
                                sm: columnIndex < 2 ? "block" : "none",
                                md: columnIndex < 3 ? "block" : "none",
                                lg: "block",
                            },
                            minWidth: 0,
                            height: "100%",
                            p: 1.5,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                            boxShadow: (theme) =>
                                theme.palette.mode === "dark"
                                    ? "0 4px 18px rgba(0, 0, 0, 0.2)"
                                    : "0 4px 18px rgba(20, 35, 25, 0.035)",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                mb: 1.5,
                                px: 0.5,
                            }}
                        >
                            <Skeleton
                                variant="text"
                                width={columnIndex === 0 ? "42%" : "56%"}
                                height={32}
                            />
                            <Skeleton
                                variant="rounded"
                                width={30}
                                height={30}
                                sx={{ flexShrink: 0, borderRadius: 1.5 }}
                            />
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                            }}
                        >
                            {[0, 1, 2, 3, 4, 5].map((itemIndex) => (
                                <Box
                                    key={itemIndex}
                                    sx={{
                                        minHeight: 60,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.25,
                                        p: 1,
                                        borderRadius: 2.25,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        backgroundColor: "background.paper",
                                    }}
                                >
                                    <Skeleton
                                        variant="rounded"
                                        width={40}
                                        height={40}
                                        sx={{
                                            flexShrink: 0,
                                            borderRadius: 2,
                                        }}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Skeleton
                                            variant="text"
                                            width={
                                                itemIndex % 2 === 0
                                                    ? "72%"
                                                    : "56%"
                                            }
                                            height={22}
                                        />
                                        <Skeleton
                                            variant="text"
                                            width="34%"
                                            height={16}
                                        />
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

export default function FileFinderPage() {
    const history = useHistory();
    const location = useLocation();
    const classes = useStyles();

    // Files and folders from firebase
    const { files, filesLoaded, folders, foldersLoaded, groups, groupsLoaded } =
        useContext(DatabaseContext);

    const { currentUserData } = useContext(UserContext);

    const [foldersFlat, setFoldersFlat] = useState([]);

    const [columns, setColumns] = useState([]);
    const [navigatorIsReady, setNavigatorIsReady] = useState(false);
    const [preservedTrailingWidth, setPreservedTrailingWidth] = useState(0);

    const [selectedColumnIndex, setSelectedColumnIndex] = useState([]); // Just used to show visually which is selected

    const scrollRef = useRef();
    const scrollAnimationFrameRef = useRef(null);
    const scrollSnapRestoreRef = useRef(null);

    useEffect(() => {
        scrollSnapRestoreRef.current?.();
        scrollSnapRestoreRef.current = null;

        const startFrameId = window.requestAnimationFrame(() => {
            const scrollbar = scrollRef.current;

            if (!scrollbar) {
                return;
            }

            const view = scrollbar.view;
            const start = scrollbar.getScrollLeft
                ? scrollbar.getScrollLeft()
                : view?.scrollLeft || 0;
            const scrollWidth = scrollbar.getScrollWidth
                ? scrollbar.getScrollWidth()
                : view?.scrollWidth || 0;
            const clientWidth = scrollbar.getClientWidth
                ? scrollbar.getClientWidth()
                : view?.clientWidth || 0;
            const target = Math.max(
                0,
                scrollWidth - clientWidth - preservedTrailingWidth,
            );
            const distance = target - start;
            const prefersReducedMotion = window.matchMedia?.(
                "(prefers-reduced-motion: reduce)",
            ).matches;

            const setScrollLeft = (value) => {
                if (scrollbar.scrollLeft) {
                    scrollbar.scrollLeft(value);
                } else if (view) {
                    view.scrollLeft = value;
                }
            };

            if (prefersReducedMotion || Math.abs(distance) < 1) {
                setScrollLeft(target);
                setPreservedTrailingWidth(0);
                return;
            }

            if (view) {
                const previousScrollSnapType = view.style.scrollSnapType;
                const previousWebkitScrollSnapType =
                    view.style.WebkitScrollSnapType;

                view.style.scrollSnapType = "none";
                view.style.WebkitScrollSnapType = "none";
                scrollSnapRestoreRef.current = () => {
                    view.style.scrollSnapType = previousScrollSnapType;
                    view.style.WebkitScrollSnapType =
                        previousWebkitScrollSnapType;
                };
            }

            const duration = 280;
            let animationStart;

            const animateScroll = (timestamp) => {
                if (animationStart === undefined) {
                    animationStart = timestamp;
                }

                const progress = Math.min(
                    1,
                    (timestamp - animationStart) / duration,
                );
                const easedProgress = 1 - Math.pow(1 - progress, 3);

                setScrollLeft(start + distance * easedProgress);

                if (progress < 1) {
                    scrollAnimationFrameRef.current =
                        window.requestAnimationFrame(animateScroll);
                } else {
                    scrollSnapRestoreRef.current?.();
                    scrollSnapRestoreRef.current = null;
                    setPreservedTrailingWidth(0);
                }
            };

            scrollAnimationFrameRef.current =
                window.requestAnimationFrame(animateScroll);
        });
        scrollAnimationFrameRef.current = startFrameId;

        return () => {
            if (scrollAnimationFrameRef.current !== null) {
                window.cancelAnimationFrame(scrollAnimationFrameRef.current);
            }
            scrollSnapRestoreRef.current?.();
            scrollSnapRestoreRef.current = null;
        };
    }, [columns.length, location.pathname, preservedTrailingWidth]);

    const [statusIsVisible, setStatusIsVisible] = useState(false);
    const [statusType, setStatusType] = useState("error");
    const [statusMessage, setStatusMessage] = useState("");

    const flatFolderArrayFiltered = useMemo(() => {
        return getFlatFolderArray(folders, files, currentUserData, groups);
    }, [folders, files, currentUserData, groups]);

    const databaseIsReady = Boolean(
        currentUserData && filesLoaded && foldersLoaded && groupsLoaded,
    );

    useEffect(() => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
            // detaching the listener
            if (!user) {
                // If no user is logged in, redirect to sign in page
                // history.replace("/signin");
                history.replace("/signin?url=" + location.pathname);
            }
        });

        if (!databaseIsReady) {
            setNavigatorIsReady(false);
            return () => unsubscribe();
        }

        if (currentUserData) {
            // flatArrayFiltered now contains all the folders that the user should have access to. Now make a nested folder structure from this.

            let nestedFolderArray = createNestedFolderStructure(
                flatFolderArrayFiltered,
            );

            // Get the current folder key from the browser url
            let currentFolderKey = location.pathname.replace("/files/", "");

            if (currentFolderKey === "/files") {
                let newColumns = [
                    {
                        id: "Root",
                        name: "Everything",
                        folders: nestedFolderArray,
                        folder: { name: "Root", id: "root" },
                    },
                ];

                setColumns(newColumns);
            } else {
                // A folder is already selected (so user loaded this page with a folder key in the url)
                // Have to open the UI to this folder

                const folderMap = new Map();

                flatFolderArrayFiltered.forEach((folder) =>
                    folderMap.set(folder.id, folder),
                );

                let currentFolder = folderMap.get(currentFolderKey);

                if (currentFolder) {
                    // Add the root folder first as a column
                    let newColumns = [
                        {
                            id: "Root",
                            name: "Everything",
                            folders: nestedFolderArray,
                            folder: { name: "Root", id: "root" },
                        },
                    ];

                    // Then get all the folder between the selected one and the root,
                    // add them as columns
                    let parents = [];
                    getParents(currentFolder, parents);

                    parents.reverse().forEach((parent) => {
                        let matchingFolder = folderMap.get(parent.id);

                        if (matchingFolder) {
                            newColumns.push({
                                id: matchingFolder.id,
                                name: matchingFolder.name,
                                folders: matchingFolder.folders,
                                folder: matchingFolder,
                            });
                        }
                    });

                    // Then add the selected folder as a column

                    newColumns.push({
                        id: currentFolder.id,
                        name: currentFolder.name,
                        folders: currentFolder.folders,
                        folder: currentFolder,
                    });

                    // Assign visible files to each folder. Favourites is a
                    // virtual folder, so it is populated from the user's keys
                    // rather than a file.folder match.
                    newColumns.forEach((column) => {
                        column.files = getFilesForFinderColumn(
                            column.id,
                            files,
                            groups,
                            currentUserData,
                        );
                    });

                    // Now Set the selected folder index for each column, so the UI displays correctly
                    newColumns.forEach((column) => {
                        column.folders.forEach((folder, folderIndex) => {
                            if (
                                currentFolder.ancestors.includes(folder.id) ||
                                currentFolder.id === folder.id ||
                                currentFolder.parent === folder.id
                            ) {
                                column.selectedFolderIndex = folderIndex;
                            }
                        });
                    });

                    setSelectedColumnIndex(newColumns.length - 2);
                    setColumns(newColumns);
                }
            }

            setFoldersFlat(flatFolderArrayFiltered);
            setNavigatorIsReady(true);
        }

        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        databaseIsReady,
        files,
        flatFolderArrayFiltered,
        folders,
        currentUserData,
    ]);

    function getParents(folderNode, parentNodeTrail) {
        let parentNode = folderNode.parentNode;

        if (parentNode === undefined || parentNode.id === undefined) {
            return;
        } else {
            parentNodeTrail.push({
                id: parentNode.id,
            });
            getParents(parentNode, parentNodeTrail);
        }
    }

    function getFlatFolderArray(folders, files, currentUserData, groups) {
        if (!folders || !files || !currentUserData) return [];

        // Create a map for quick folder lookup by key
        const folderMap = new Map();
        folders.forEach((folder) => {
            folderMap.set(folder.key, {
                key: folder.key,
                id: folder.key,
                name: folder.name,
                hub: folder.hub,
                parent: folder.parent,
                imageURL: folder.imageURL,
                parentNode: {},
                folders: [],
                groups: folder.groups,
                managers: folder.managers,
                users: folder.users,
                ancestors: folder.ancestors,
                createdBy: folder.createdBy,
            });
        });

        // Set to keep track of folders we want to keep (avoid duplicates)
        const folderSet = new Set();

        // Helper to add folder and its ancestors
        function addFolderAndAncestors(folderKey) {
            if (!folderKey || folderSet.has(folderKey)) return;

            const folder = folderMap.get(folderKey);
            if (!folder) return;

            folderSet.add(folderKey);

            // Recursively add ancestors
            if (folder.ancestors && folder.ancestors.length) {
                folder.ancestors.forEach((ancestorKey) =>
                    addFolderAndAncestors(ancestorKey),
                );
            }
        }

        // Add folders user can access (by managers, hub users, or createdBy)
        folderMap.forEach((folder) => {
            let shouldAdd = false;

            if (
                folder.managers.includes(currentUserData.key) ||
                currentUserData.role === "System Digital Manager"
            ) {
                shouldAdd = true;
            } else if (
                folder.hub &&
                folder.users.includes(currentUserData.key)
            ) {
                shouldAdd = true;
            } else if (folder.createdBy === currentUserData.key) {
                shouldAdd = true;
            }

            if (shouldAdd) {
                addFolderAndAncestors(folder.key);
            }
        });

        // Also add folders that are ancestors of files the user can see
        files.forEach((file) => {
            if (shouldFileBeVisible(file, groups, currentUserData)) {
                if (file.ancestors && file.ancestors.length) {
                    file.ancestors.forEach((ancestorKey) =>
                        addFolderAndAncestors(ancestorKey),
                    );
                }
            }
        });

        // Convert Set to array
        let flatFolderArrayFiltered = Array.from(folderSet).map((key) =>
            folderMap.get(key),
        );

        // Sort alphabetically by name
        flatFolderArrayFiltered.sort((a, b) => a.name.localeCompare(b.name));

        // Add the favourites folder at the front
        const favesFolder = {
            id: FAVOURITES_ID,
            key: FAVOURITES_ID,
            name: "Favourites",
            parent: "Root",
            ancestors: [],
            folders: [],
            files: [],
            hub: false,
            parentNode: {},
            users: [],
            managers: [],
        };

        flatFolderArrayFiltered.unshift(favesFolder);

        return flatFolderArrayFiltered;
    }

    function createNestedFolderStructure(flatFolderArray) {
        let nestedFolderArray = [];

        for (let i = 0; i < flatFolderArray.length; i++) {
            let folder = flatFolderArray[i];

            if (folder.parent === "Root") {
                nestedFolderArray.push(folder);
            } else {
                let parentFolder = flatFolderArray.find(
                    (anotherFolder) => anotherFolder.key === folder.parent,
                );

                if (parentFolder !== undefined) {
                    parentFolder.folders.push(folder);
                    folder.parentNode = parentFolder;
                }
            }
        }

        return nestedFolderArray;
    }

    function handleFolderSelect(columnIndex, folderIndex) {
        let selectedColumn = columns[columnIndex];
        let selectedFolder = selectedColumn.folders[folderIndex];

        setSelectedColumnIndex(columnIndex);

        // Set columns for UI
        let newColumns = [];
        for (let i = 0; i < columnIndex + 1; i++) {
            newColumns.push(columns[i]);
        }

        newColumns.push({
            id: selectedFolder.id,
            name: selectedFolder.name,
            folders: selectedFolder.folders,
            folder: selectedFolder,
            selectedFolderIndex: 0,
        });

        // Have to add files to each column
        newColumns.forEach((column) => {
            column.files = getFilesForFinderColumn(
                column.id,
                files,
                groups,
                currentUserData,
            );
        });

        history.push(`/files/${selectedFolder.id}`);

        // Add an empty column for scrolling purposes
        newColumns.push({
            id: makeid(15),
            name: "",
            folders: [],
            selectedFolderIndex: 0,
            isSpacer: true,
        });

        const scrollbar = scrollRef.current;
        const view = scrollbar?.view;
        const clientWidth = scrollbar?.getClientWidth
            ? scrollbar.getClientWidth()
            : view?.clientWidth || 0;
        const currentScrollLeft = scrollbar?.getScrollLeft
            ? scrollbar.getScrollLeft()
            : view?.scrollLeft || 0;
        const columnElement = view?.querySelector(
            "[data-finder-column='true']:not([data-finder-spacer='true'])",
        );
        const isMobile =
            window.matchMedia?.("(max-width: 599.95px)").matches ??
            window.innerWidth < 600;
        const columnWidth =
            columnElement?.getBoundingClientRect().width ||
            clientWidth * (isMobile ? 1 : 0.24);
        const visibleNextColumnCount = newColumns.filter(
            (column) => !column.isSpacer || !isMobile,
        ).length;
        const nextNaturalScrollWidth = Math.max(
            clientWidth,
            visibleNextColumnCount * columnWidth,
        );
        const widthRequiredToRetainPosition = Math.max(
            nextNaturalScrollWidth,
            currentScrollLeft + clientWidth,
        );

        setPreservedTrailingWidth(
            Math.max(0, widthRequiredToRetainPosition - nextNaturalScrollWidth),
        );

        setColumns(newColumns);
    }

    function onDragEnd(result) {
        let column = columns.find((c) => c.id === result.source.droppableId);

        if (column) {
            // Determine if we are dragging a file or folder
            if (
                column.folders.filter((f) => f.key === result.draggableId)
                    .length > 0
            ) {
                didFinishDraggingFolder(result);
            } else if (
                column.files.filter((f) => f.key === result.draggableId)
                    .length > 0
            ) {
                didFinishDraggingFile(result);
            }
        }
    }

    function didFinishDraggingFile(result) {
        const { destination, source } = result;

        // Do some checks first to whether the drop should take place
        if (!destination) {
            return;
        }

        let destinationColumnId = destination.droppableId;
        let sourceColumnId = source.droppableId;

        if (
            destinationColumnId === sourceColumnId &&
            destination.index === source.index
        ) {
            return;
        }

        if (destinationColumnId === "Root") {
            showStatusMessage("Can't move a file here", "error");
            return;
        }

        let destinationFolder = foldersFlat.find(
            (f) => f.key === destinationColumnId,
        );

        if (!destinationFolder) {
            return;
        }

        if (!destinationFolder.managers.includes(currentUserData.key)) {
            if (currentUserData.role !== "System Digital Manager") {
                showStatusMessage(
                    "Cannot move the file here, you are not a manager of the destination folder",
                    "error",
                );

                return;
            }
        }

        let file = files.find((f) => f.key === result.draggableId);

        if (!file.managers.includes(currentUserData.key)) {
            if (currentUserData.role !== "System Digital Manager") {
                showStatusMessage(
                    "You are not a manager of this file",
                    "error",
                );

                return;
            }
        }

        // End checks

        if (file && destinationFolder) {
            let fileRef = firebase.database().ref("files").child(file.key);

            let newAncestors = jsonFromArray(destinationFolder.ancestors);
            newAncestors[destinationFolder.id] = true;

            // Update the files parents and ancestors in firebase
            fileRef.update({
                folder: destinationFolder.id,
                ancestors: newAncestors,
                managers: jsonFromArray(destinationFolder.managers),
            });
        }
    }

    function didFinishDraggingFolder(result) {
        const { destination, source } = result;

        // Do some checks first to whether the drop should take place
        if (!destination) {
            return;
        }

        let draggedFolderId = result.draggableId;
        let sourceColumnId = source.droppableId;
        let destinationColumnId = destination.droppableId;

        if (
            destinationColumnId === sourceColumnId &&
            destination.index === source.index
        ) {
            return;
        }

        if (destinationColumnId === "Root") {
            showStatusMessage("Can't move a folder here", "error");
            return;
        }

        if (draggedFolderId === destinationColumnId) {
            showStatusMessage("Can't move a folder into itself", "error");

            return;
        }

        let destinationFolder = foldersFlat.find(
            (f) => f.key === destinationColumnId,
        );

        if (!destinationFolder) {
            return;
        }

        if (!destinationFolder.managers.includes(currentUserData.key)) {
            if (currentUserData.role !== "System Digital Manager") {
                showStatusMessage(
                    "Cannot move the folder here, you are not a manager of the destination folder",
                    "error",
                );

                return;
            }
        }

        let sourceFolder = foldersFlat.find((f) => f.key === sourceColumnId);

        let draggedFolder = foldersFlat.find((f) => f.key === draggedFolderId);

        if (!draggedFolder.managers.includes(currentUserData.key)) {
            if (currentUserData.role !== "System Digital Manager") {
                showStatusMessage(
                    "You are not a manager of this folder",
                    "error",
                );

                return;
            }
        }

        if (draggedFolder.hub) {
            if (!destinationFolder.hub) {
                showStatusMessage("Can't move a hub into a folder", "error");
                return;
            }
        }
        // End checks

        if (destinationFolder && sourceFolder && draggedFolder) {
            draggedFolder.parent = destinationFolder.key;
            draggedFolder.ancestors = [...destinationFolder.ancestors];
            draggedFolder.managers = [...destinationFolder.managers];
            // draggedFolder.users = [...destinationFolder.users];

            // For any folder that is a child of the dragged folder, thier parent remains the same, but we need to
            //  work out their ancestors
            // Start by getting all folders that are below dragged folder in the hierachy, and sort them by number
            // of ancestors i.e. they are sorted in their order in the file system
            let childFolders = foldersFlat
                .filter((f) => f.ancestors.includes(draggedFolder.key))
                .sort((a, b) => {
                    if (a.ancestors.length > b.ancestors.length) return 1;
                    if (a.ancestors.length < b.ancestors.length) return -1;
                    return 0;
                });

            childFolders.forEach((childFolder, index) => {
                if (index === 0) {
                    childFolder.ancestors = [
                        ...draggedFolder.ancestors,
                        draggedFolder.parent,
                        childFolder.parent,
                    ];

                    childFolder.managers = draggedFolder.managers;
                    // childFolder.users = draggedFolder.users;
                } else if (index > 0) {
                    let previousFolder = childFolders[index - 1];
                    if (previousFolder) {
                        if (childFolder.parent === previousFolder.parent) {
                            childFolder.ancestors = [
                                ...previousFolder.ancestors,
                            ];
                            childFolder.managers = previousFolder.managers;
                            // childFolder.users = previousFolder.users;
                        } else {
                            let parentOfChild = childFolders.find(
                                (f) => f.key === childFolder.parent,
                            );
                            if (parentOfChild) {
                                childFolder.ancestors = [
                                    ...parentOfChild.ancestors,
                                    childFolder.parent,
                                ];

                                childFolder.managers = parentOfChild.managers;
                                // childFolder.users = parentOfChild.users;
                            }
                        }
                    }
                }
            });

            // All the child folders ancestors have been set, now write to firebase

            var foldersRef = firebase.database().ref("folders");

            // Child Folders to firebase

            childFolders.forEach((childFolder) => {
                // Update the childFolder ancestors in firebase
                foldersRef
                    .child(childFolder.id)

                    .update({
                        ancestors: jsonFromArray(childFolder.ancestors),
                        managers: jsonFromArray(childFolder.managers),
                        // users: jsonFromArray(childFolder.users),
                    });

                // Files in this child folder

                files
                    .filter((f) => f.folder === childFolder.id)
                    .forEach((file) => {
                        file.ancestors = [
                            ...childFolder.ancestors,
                            childFolder.key,
                        ];
                        let fileRef = firebase
                            .database()
                            .ref("files")
                            .child(file.key);

                        fileRef.update({
                            ancestors: jsonFromArray(file.ancestors),
                            managers: jsonFromArray(childFolder.managers),
                        });
                    });
            });

            let draggedFolderNewAncestors = jsonFromArray(
                destinationFolder.ancestors,
            );
            draggedFolderNewAncestors[destinationFolder.key] = true;

            // Dragged folder to firebase
            foldersRef.child(draggedFolderId).update({
                parent: destinationFolder.key,
                ancestors: draggedFolderNewAncestors,
                managers: jsonFromArray(destinationFolder.managers),
                // users: jsonFromArray(destinationFolder.users),
            });

            // Files in the dragged folder
            files
                .filter((f) => f.folder === draggedFolderId)
                .forEach((file) => {
                    file.ancestors = [
                        ...destinationFolder.ancestors,
                        destinationFolder.key,
                        draggedFolder.key,
                    ];
                    let fileRef = firebase
                        .database()
                        .ref("files")
                        .child(file.key);

                    fileRef.update({
                        ancestors: jsonFromArray(file.ancestors),
                        managers: jsonFromArray(destinationFolder.managers),
                    });
                });
        }
    }

    const handleStatusClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setStatusIsVisible(false);
    };

    function showStatusMessage(message, type) {
        setStatusType(type);
        setStatusMessage(message);
        setStatusIsVisible(true);
    }

    return (
        <div className={classes.root}>
            {!navigatorIsReady ? (
                <FinderLoadingState />
            ) : (
                <Scrollbars
                    className={classes.scrollbar}
                    renderView={(viewProps) => (
                        <div
                            {...viewProps}
                            className={`${viewProps.className || ""} ${classes.scrollView}`}
                        />
                    )}
                    ref={scrollRef}
                >
                    <DragDropContext onDragEnd={onDragEnd}>
                        <div className={classes.columnsContainer}>
                            {columns.map((column, index) => (
                                <div
                                    className={`${classes.column} ${
                                        column.isSpacer
                                            ? classes.spacerColumn
                                            : classes.columnEntering
                                    }`}
                                    key={column.id}
                                    data-finder-column="true"
                                    data-finder-spacer={
                                        column.isSpacer ? "true" : undefined
                                    }
                                >
                                    <FinderColumn
                                        key={column.id}
                                        column={column}
                                        index={index}
                                        handleFolderSelect={handleFolderSelect}
                                        selectedColumnIndex={
                                            selectedColumnIndex
                                        }
                                        showStatusMessage={showStatusMessage}
                                    />
                                </div>
                            ))}
                            {preservedTrailingWidth > 0 && (
                                <div
                                    aria-hidden="true"
                                    style={{
                                        flex: `0 0 ${preservedTrailingWidth}px`,
                                        width: preservedTrailingWidth,
                                        minWidth: preservedTrailingWidth,
                                        height: "100%",
                                        visibility: "hidden",
                                        pointerEvents: "none",
                                    }}
                                />
                            )}
                        </div>
                    </DragDropContext>
                </Scrollbars>
            )}
            <Snackbar
                open={statusIsVisible}
                autoHideDuration={6000}
                onClose={handleStatusClose}
            >
                <Alert onClose={handleStatusClose} severity={statusType}>
                    {statusMessage}
                </Alert>
            </Snackbar>
            <ToolbarWithLogo />
        </div>
    );
}
